class CampSteps extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.stepperItems = Array.from(this.querySelectorAll('[data-stepper-item]'));
    this.current = 0;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-step-next]')) {
        this.next();
      } else if (event.target.closest('[data-step-back]')) {
        this.goTo(this.current - 1);
      }
    });
  }

  validateStep(index) {
    const fields = this.steps[index].querySelectorAll('input, select, textarea');

    for (const field of fields) {
      if (!field.checkValidity()) {
        field.reportValidity();
        return false;
      }
    }

    return true;
  }

  next() {
    if (this.validateStep(this.current)) {
      this.goTo(this.current + 1);
    }
  }

  goTo(index) {
    if (index < 0 || index >= this.steps.length) return;

    this.current = index;
    this.steps.forEach((step, stepIndex) => (step.hidden = stepIndex !== index));
    this.setStepper(index);

    const modal = this.closest('dialog');
    if (modal) {
      modal.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  setStepper(index) {
    this.stepperItems.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
      item.classList.toggle('is-complete', itemIndex < index);
    });
  }
}

class CampBookingForm extends CampSteps {
  connectedCallback() {
    super.connectedCallback();
    this.errorElement = this.querySelector('[data-error]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.initCountry();
  }

  initCountry() {
    const wrapper = this.querySelector('[data-camp-country]');
    if (!wrapper) return;

    const select = wrapper.querySelector('select');
    const flag = wrapper.querySelector('.camp-country__flag');
    const { defaultCountry, defaultCode } = wrapper.dataset;
    const codes = new Map();

    try {
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      for (let first = 65; first <= 90; first++) {
        for (let second = 65; second <= 90; second++) {
          const code = String.fromCharCode(first, second);
          const name = regionNames.of(code);
          if (name && name !== code) codes.set(name.toLowerCase(), code);
        }
      }
    } catch (error) {}

    if (defaultCountry && defaultCode) codes.set(defaultCountry.toLowerCase(), defaultCode);

    const updateFlag = () => {
      const code = codes.get(select.value.toLowerCase());
      if (code) flag.src = `https://flagcdn.com/${code.toLowerCase()}.svg`;
      flag.hidden = !code;
      wrapper.classList.toggle('has-flag', Boolean(code));
    };

    if (!select.value && defaultCountry) {
      select.value = defaultCountry;
      if (select.selectedIndex === -1) select.selectedIndex = 0;
    }

    select.addEventListener('change', updateFlag);
    updateFlag();
  }

  showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }

  async onSubmit(event) {
    event.preventDefault();

    const data = new FormData(this.form);
    const room = this.form.querySelector('input[name="room"]:checked');
    const submitButton = event.submitter || this.form.querySelector('[type="submit"]');

    if (!room) {
      this.showError('Please choose a room.');
      return;
    }

    const properties = {
      _camp_booking: this.dataset.campHandle,
      'Total camp price': room.dataset.roomPrice,
      'Balance due': this.dataset.balanceDue,
    };

    const attributes = {
      Camp: this.dataset.campTitle,
      'Camp dates': this.dataset.campDates,
      Room: room.dataset.roomTitle,
      'Total camp price': room.dataset.roomPrice,
      'Balance due': this.dataset.balanceDue,
      Participant: `${data.get('first_name')} ${data.get('last_name')}`.trim(),
      Email: data.get('email'),
      Phone: data.get('phone'),
      Country: data.get('country'),
    };

    [properties, attributes].forEach((object) => {
      Object.keys(object).forEach((key) => {
        if (!object[key]) delete object[key];
      });
    });

    const root = window.Shopify?.routes?.root || '/';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

    this.showError('');
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    this.setStepper(this.stepperItems.length - 1);

    try {
      if (this.dataset.clearCart === 'true') {
        await fetch(`${root}cart/clear.js`, { method: 'POST', headers });
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: [{ id: Number(room.value), quantity: 1, properties }],
        }),
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.description || json.message);
      }

      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ attributes }),
      });

      window.location.href = `${root}checkout?checkout[email]=${encodeURIComponent(data.get('email'))}`;
    } catch (error) {
      this.showError(error.message || 'Something went wrong, please try again.');
      this.setStepper(this.current);
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
  }
}

class CampParticipantForm extends CampSteps {
  connectedCallback() {
    super.connectedCallback();
    if (!this.form) return;

    const storageKey = 'campParticipantAccess';
    const urlParams = Object.fromEntries(new URLSearchParams(window.location.search));
    const hash = window.location.hash.replace(/^#/, '');
    if (hash.includes('&')) {
      const hashParts = hash.split('&');
      if (hashParts[0] && !hashParts[0].includes('=')) urlParams.order = hashParts[0];
      Object.assign(urlParams, Object.fromEntries(new URLSearchParams(hashParts.slice(1).join('&'))));
    }
    if (urlParams.order) urlParams.order = urlParams.order.replace(/^#/, '');
    const hasLinkDetails = ['order', 'email', 'name', 'camp', 'room'].some(
      (key) => urlParams[key] && !String(urlParams[key]).includes('{{'),
    );
    let params = null;
    if (hasLinkDetails) {
      params = urlParams;
      sessionStorage.setItem(storageKey, JSON.stringify(params));
      window.history.replaceState(null, '', window.location.pathname);
    } else {
      try {
        params = JSON.parse(sessionStorage.getItem(storageKey));
      } catch (error) {}
    }

    const fieldMap = {
      order: 'contact[Order number]',
      camp: 'contact[Camp]',
      room: 'contact[Room]',
      name: 'contact[name]',
      email: 'contact[email]',
    };

    if (params) {
      Object.entries(fieldMap).forEach(([param, name]) => {
        const field = this.form.elements[name];
        if (field && params[param] && !String(params[param]).includes('{{') && !field.value) field.value = params[param];
      });
    }

    this.form.addEventListener('submit', () => {
      this.querySelectorAll('[data-join-output]').forEach((output) => {
        const group = output.dataset.joinOutput;
        output.value = Array.from(this.querySelectorAll(`[data-join="${group}"]:checked`))
          .map((input) => input.value)
          .join(', ');
      });

      const text = (name) => {
        const field = this.form.elements[name];
        return (field && field.value ? field.value : '').trim();
      };
      const line = (label, name) => `${label}: ${text(name) || 'Not provided'}`;
      const lines = [
        'New camp participant',
        '',
        'Booking',
        line('Order number', 'contact[Order number]'),
        line('Camp', 'contact[Camp]'),
        line('Room', 'contact[Room]'),
        '',
        'Personal details',
        line('Full name', 'contact[name]'),
        line('Date of birth', 'contact[Date of birth]'),
        line('Nationality', 'contact[Nationality]'),
        line('Emergency contact', 'contact[Emergency contact name]'),
        line('Emergency phone', 'contact[Emergency contact phone]'),
        '',
        'Riding',
        line('Riding level', 'contact[Riding level]'),
        line('Own bike', 'contact[Own bike]'),
        line('Riding notes', 'contact[Riding notes]'),
        '',
        'Health',
        line('Dietary requirements', 'contact[Dietary requirements]'),
        line('Jersey size', 'contact[Jersey size]'),
        line('Health notes', 'contact[Health notes]'),
      ];
      const body = this.form.elements['contact[body]'];
      if (body) body.value = lines.join('\n');

      Array.from(this.form.elements).forEach((field) => {
        if (!field.name || field.name === 'contact[body]' || field.name === 'contact[email]') return;
        if (field.name.startsWith('contact[')) field.removeAttribute('name');
      });
    });
  }
}

if (!window.campBookingModalInit) {
  window.campBookingModalInit = true;

  const getModal = (sectionId) =>
    document.querySelector(
      sectionId ? `[data-camp-booking-modal][data-section-id="${sectionId}"]` : '[data-camp-booking-modal]',
    );

  const openModal = (modal, opener) => {
    if (!modal || modal.open) return;
    modal.opener = opener || null;
    modal.showModal();
  };

  let pressTarget = null;
  document.addEventListener('pointerdown', (event) => (pressTarget = event.target));

  document.addEventListener('click', (event) => {
    const weeksLink = event.target.closest('[data-camp-weeks-scroll]');
    if (weeksLink) {
      const weeks = document.getElementById('camp-weeks');
      if (weeks) {
        event.preventDefault();
        weeks.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const opener = event.target.closest('[data-camp-booking-open]');
    if (opener) {
      event.preventDefault();
      const handle = opener.getAttribute('data-camp-product');
      const modal = handle
        ? document.querySelector(`[data-camp-booking-modal][data-camp-handle="${handle}"]`)
        : getModal();
      openModal(modal, opener);
      return;
    }

    const modal = event.target.closest('[data-camp-booking-modal]');
    if (!modal) return;

    const backdropClick = event.target === modal && pressTarget === modal;
    if (backdropClick || event.target.closest('[data-camp-booking-close]')) {
      modal.close();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches?.('[data-camp-booking-modal]')) {
        event.target.opener?.focus();
      }
    },
    true,
  );

  document.addEventListener('shopify:section:select', (event) => {
    openModal(getModal(event.detail.sectionId));
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    document
      .querySelectorAll(`[data-camp-booking-modal][data-section-id="${event.detail.sectionId}"]`)
      .forEach((modal) => modal.close());
  });
}

if (!customElements.get('camp-booking-form')) {
  customElements.define('camp-booking-form', CampBookingForm);
}

if (!customElements.get('camp-participant-form')) {
  customElements.define('camp-participant-form', CampParticipantForm);
}
